(function (angular) {
    'use strict';
    var app = angular.module('enervee');

    /**
     * Manages rebate process and callbacks for the checkout card
     */
    app.factory('EnerveeRebateService', [
        '$log',
        '$q',
        '$window',
        'CartService',
        'UtilService',
        'CmsContentService',
        'Upload',
        'RebateApiConsumerService',
        'EnerveeCheckoutService',
        'ProductApiConsumerService',
        'WITH_CREDENTIALS',
        function (
            $log,
            $q,
            $window,
            CartService,
            UtilService,
            CmsContentService,
            Upload,
            RebateApiConsumerService,
            EnerveeCheckoutService,
            ProductApiConsumerService,
            WITH_CREDENTIALS
        ) {
            var _pub = {
                formClosedReasons: {
                    FORM_COMPLETE: "formComplete",
                    DISMISS: "dismiss",
                    SERVER_ERROR: "serverError"
                },
                hasShippingData: false,
            };
            var authorization_code;
            var applicationId = null;
            var validationURL = '';
            var applicationFormOpenFunc = function () {
                $log.error("No verification form has been registered!");
                return $q.reject({});
            };

            var error500Message = {};
            var fileTooLargeErrorMessage;
            CmsContentService.retrieveCMSValue(error500Message, 'title', 'base:modals:rrapi_error_500_title');
            CmsContentService.retrieveCMSValue(error500Message, 'body', 'base:modals:rrapi_error_500_body');
            CmsContentService.getMarketPlaceText('rebate_fast_track:file_too_large_error_message').then(function (text) {
                fileTooLargeErrorMessage = text;
            });

            /**
             * Makes a request to the rebates api proxied through enervee root.
             * @param path
             * @param context
             * @param method
             *
             */
            _pub.requestData = function (path, context, method) {
                var deferred = $q.defer();

                method = method || 'GET';
                path = 'rebate-api/api/1.0/' + path;
                var context = context || {};
                var pathAndContext = UtilService.addContextToUrl(path, context);
                var options = {
                    method: method,
                    url: pathAndContext.path,
                    ...WITH_CREDENTIALS,
                };

                if (method === 'GET') {
                    options.params = pathAndContext.context;
                } else {
                    options.data = pathAndContext.context;
                    options.params = {};
                }

                RebateApiConsumerService.request(options).then(function success(response) {
                    deferred.resolve(response.data);
                }).catch(function error(response, status) {
                    if (status >= 500) {
                        _pub.openErrorModal();
                    }
                    deferred.reject(response);
                });

                return deferred.promise;
            };

            /**
             * Gets order authorization based on the current application id.
             */
            _pub.retrieveOrderAuthorization = function () {

                var context = {};
                context.application_id = applicationId;

                // @TODO: /authorize must move after {application_id} after integration with its API (mocks can not recognize it that way now)
                var url = 'applications/authorize/{application_id}';

                return _pub.requestData(url, context, 'POST').then(function (responseData) {
                    authorization_code = responseData.authorization_code;
                    return responseData;
                });
            };


            /**
             * Check if application is valid.
             */
            _pub.checkApplicationValid = function () {

                var context = {};
                context.application_id = applicationId;

                var url = 'applications/{application_id}/';

                return _pub.requestData(url, context, 'GET').then(function (responseData) {
                    _pub.hasShippingData = responseData.has_shipping_data || false;
                    return responseData.is_valid;
                });
            };


            _pub.getValidRebatesFromApplication = function (formResponseData) {
                var validRebatesCount = 0;
                var invalidRebatesCount = 0;

                formResponseData.rebated_products.forEach(function (rebate) {
                    if (rebate.is_valid) {
                        validRebatesCount += rebate.quantity;
                    } else {
                        var eligibleRebates = _pub.getEligibleRebatesFromReason(rebate.reason);
                        invalidRebatesCount += rebate.quantity - eligibleRebates;
                        validRebatesCount += eligibleRebates;
                    }
                });

                return {
                    validRebatesCount: validRebatesCount,
                    invalidRebatesCount: invalidRebatesCount,
                };
            };

            /**
             * Takes a `reason` object from the RRAPI:Applications:Form endpoint and returns the
             * rebates that the user is eligible for, which could be 0.
             *
             * @param {Object} reason the `reason` property from a rebated_products object
             * @param {Number} [reason.eligible_rebate_quantity]
             * @param {Number} reason.max_rebate_quantity used if the eligible_rebate_quantity is
             * undefined in the reason
             * @return {Number} the number of rebates the user is eligible for
             */
            _pub.getEligibleRebatesFromReason = function (reason) {
                if (!reason) {
                    return 0;
                }
                var eligibleRebates = reason.eligible_rebate_quantity;
                if (angular.isUndefined(eligibleRebates)) {
                    eligibleRebates = reason.max_rebate_quantity;
                }
                return Math.max(eligibleRebates, 0);
            };

            /**
             * Opens an alert modal with a generic "please try again later message"
             */
            _pub.openErrorModal = function () {
                UtilService.openAlertModal({
                    title: error500Message.title,
                    body: error500Message.body,
                });
            };

            /**
             * Submits the fast track products to claim to create application on RRAPI.
             * Success or errors are passed along appropriately.
             * @param productArray
             * @returns {Promise}
             */
            _pub.submitProductsToClaim = function (productArray) {
                var data = {
                    products: formatProductDataForClaimSubmission(productArray),
                    utility_id: UtilService.getSetting('utilityId'),
                    context: {
                        source: 'fast-track',
                    }
                };

                var url = 'applications/';
                return _pub.requestData(url, data, 'POST').then(
                    function (responseData) {
                        validationURL = responseData.url;
                        applicationId = responseData.application_id;
                        return responseData;
                    },
                    function (response) {
                        // Log the response and continue the rejected promise
                        $log.error(response);
                        return $q.reject(response);
                    }
                );
            };

            /**
             * Return validation URL for iFrame source
             */
            _pub.getValidationURL = function () {
                return validationURL;
            };

            _pub.setValidationData = function (data) {
                validationURL = data.url;
                applicationId = data.application_id;
            };

            /**
             * Gets the best/highest rebates of the gives application id.
             *
             * @param {String} applicationId
             * @returns {Promise} a promise that resolves with the best rebates found for the
             * products in the application.
             * Example response:
             * [
             *   {
             *     "product_id": 1,
             *     "category_id": 1,
             *     "product_quantity": 1,
             *     "rebate_id": 1,
             *     "rebate_amount": 100,
             *     "rebate_quantity": 1,
             *   },
             *   {
             *     "product_id": 2,
             *     "category_id": 1,
             *     "product_quantity": 2,
             *     "rebate_id": null,
             *     "rebate_amount": null,
             *     "rebate_quantity": 0,
             *   }
             * ]
             */
            _pub.fetchBestRebates = function (applicationId) {
                _pub.applicationId = applicationId;
                const url = 'rebate-api/api/1.0/applications/best-rebates/' + _pub.applicationId + '/';
                return RebateApiConsumerService.get(url, WITH_CREDENTIALS).then(function (results) {
                    const data = EnerveeCheckoutService.transformRebateReasonsData(results.data);
                    return data;
                });
            };

            /**
             * Submits fast-track receipts and rebates list for a given application
             *
             * @param data
             */
            _pub.submitFastTrack = function (data) {
                const relativeEndpointUrl = 'rebate-api/api/1.0/applications/fast-track-form/' + _pub.applicationId + "/";
                const fullEndpointUrl = RebateApiConsumerService.getFullUrl(relativeEndpointUrl);
                const fastTrackData = formatFastTrackData(data);

                return Upload.upload({
                    url: fullEndpointUrl,
                    data: fastTrackData,
                }).catch(function(errorResponse) {
                    if (errorResponse.status === 413) {
                        return $q.reject([fileTooLargeErrorMessage]);
                    } else {
                        return $q.reject(formatFastTrackErrors(errorResponse.data));
                    }
                });
            };

            /**
             * Register a verification form's open method so that it can be called via
             * "openApplicationForm"
             *
             * @param {Function} openFunc a function that expects two arguments: "url", the
             * application form's url and "triggerSrc", where the form was triggered from
             */
            _pub.registerApplicationForm = function (openFunc) {
                applicationFormOpenFunc = openFunc;
            };

            /**
             * Open the application form by calling the registered "openFunc" that was saved in
             * "registerApplicationForm". Requests existing validation data for the user, then
             * appends that data as a query param to the form's URL.
             *
             * @param {String} triggerSrc where the application form was triggered from, fast track 2.0 or checkout
             * @param {String} formType if "reporting", then open the "reporting" page of the
             * application form.
             * @return {Promise} A promise that resolves with the return type of the openFunc
             * (typically another promise)
             */
            _pub.openApplicationForm = (triggerSrc, formType) => {
                var url = validationURL;
                if (formType === "reporting") {
                    url += "/reporting";
                }
                return getUserCustomerId().then((customerId) => {
                    if (customerId) {
                        url = UtilService.addQueryParamToURL(url, "customerId=" + customerId);
                    }
                    return applicationFormOpenFunc(url, triggerSrc);
                }).then((data) => {
                    if (typeof data.data !== "undefined") {
                        _pub.hasShippingData = _pub.hasShippingData || data.data.hasShippingData;
                    }
                    return data;
                });
            };

            /**
             * Retrieve the customer details from the fast track process.
             * @return {Object} An object containing the customer's information.
             *
             * Example response:
             *   {
             *       "phone_number": 1234567890,
             *       "address": [
             *           {
             *               "address_line_1": "5535 Westlawn Avenue",
             *               "address_line_2": "APT 211",
             *               "city": "Los Angeles",
             *               "state": "CA",
             *               "zip_code": "90066",
             *               "country": "US"
             *           }
             *       ],
             *       "last_name": "Smith",
             *       "first_name": "John",
             *       "email": "user@email.com"
             *   }
             */
            _pub.getCustomerDetails = function () {
                const url = 'rebate-api/api/1.0/customer/details/';
                return RebateApiConsumerService.get(url, WITH_CREDENTIALS)
                    .then(response => response.data);
            }

            /**
             * Retrieves the user's most recent claims from the fast track process.
             * Must have a valid JWT with a `reb_app_id` claim.
             *
             * @return {Object} Recent claim data including list of claims.
             */
            _pub.fetchCurrentRebate = function () {
                const url = 'rebate-api/api/1.0/applications/current/order/';
                return RebateApiConsumerService.get(url, WITH_CREDENTIALS);
            };

            /**
             * Gets the user's validation data, if they've completed a verification form before.
             *
             * @return {Promise} resolves with the customer's validation data or an empty object.
             */
            function getUserCustomerId() {
                var url = 'customer/external-id/';
                return _pub.requestData(url, {}).catch((err) => {
                    $log.error('Error retrieving customer external id:', err);
                    return {};
                });
            }

            /**
             * Formats the product data into the proper format for Rebate,
             * with receipts pulled out of the products.
             *
             * @param productData
             */
            function formatFastTrackData(productData) {
                var formattedData = {};
                var rebatedProducts = [];
                productData.forEach(function(rebatedProduct) {
                    var receiptData = formatReceiptData(rebatedProduct);
                    angular.extend(formattedData, receiptData.receiptImageMap);
                    rebatedProducts.push({
                        quantity: rebatedProduct.rebate_quantity,
                        rebate_id: rebatedProduct.rebate_id,
                        product_id: rebatedProduct.product_id,
                        purchase_receipts: receiptData.purchaseReceipts,
                    });
                });
                formattedData.rebated_products = Upload.json(rebatedProducts);
                return formattedData;
            }

            /**
             * Formats the receipt data for the given product, pulling the file out
             * and using a unique name to reference the file.
             *
             * @param rebatedProduct
             * @return {{receiptImageMap: Object, purchaseReceipts: Array}}
             */
            function formatReceiptData(rebatedProduct) {
                var receiptImageMap = {};
                var purchaseReceipts = [];
                if (rebatedProduct.has_rebate) {
                    rebatedProduct.receipt.images.forEach(function(image) {
                        receiptImageMap[image.$$hashKey] = image;
                        purchaseReceipts.push({
                            file_name: image.$$hashKey,
                            store_name: rebatedProduct.receipt.storeName,
                            purchase_price: rebatedProduct.receipt.purchasePrice,
                            purchase_date: formatDate(rebatedProduct.receipt.purchaseDate),
                            receipt_id: rebatedProduct.receipt.receiptID,
                        });
                    });
                }

                return {
                    receiptImageMap: receiptImageMap,
                    purchaseReceipts: purchaseReceipts,
                };
            }

            /**
             * Format the date in a useful format for Rebate.
             *
             * @param {Date} date
             * @return {string}
             */
            function formatDate(date) {
                return [
                    date.getUTCFullYear(),
                    UtilService.pad(date.getUTCMonth() + 1, 2),
                    UtilService.pad(date.getUTCDate(), 2),
                ].join('-');
            }

            /**
             * Formats the error messages returned from the Fast Track process to work better for
             * our frontend.
             *
             * @param errorData
             * @return {*}
             */
            function formatFastTrackErrors(errorData) {
                var formattedErrors = angular.copy(errorData);
                formattedErrors.rebated_products = errorData.rebated_products.map(function (productErrors) {
                    var formattedProductErrors = angular.copy(productErrors);
                    if (formattedProductErrors.purchase_receipts !== undefined) {
                        formattedProductErrors.receipt = formattedProductErrors.purchase_receipts[0];
                        delete formattedProductErrors.purchase_receipts;
                    }
                    return formattedProductErrors;
                });
                return formattedErrors;
            }

            /**
             * Format product array into a structure that RRAPI can use.
             *
             * @param productArray
             * @return {*}
             */
            function formatProductDataForClaimSubmission(productArray) {
                var productMap = {};

                productArray.forEach(function (product) {
                    if (product.id in productMap) {
                        productMap[product.id].quantity += 1;
                    } else {
                        productMap[product.id] = {
                            product_id: product.id,
                            quantity: 1,
                            category_id: product.categoryID
                        };
                    }
                });

                return UtilService.objectValues(productMap);
            }

            return _pub;
        }
    ]);
})(angular);
